/**
 * Civil time in the venue's zone — the one place BarOS decides what "today",
 * "this week" and "this month" mean.
 *
 * Everything the table stores is an instant: `WASTE#2026-09-20T13:45:12.004Z`,
 * `EXPENSE#…`, `COUNT#…`. Everything a bar *says* is civil: Sunday's service,
 * the trading week, September's invoices. Turning one into the other is the
 * whole job of this module, and it is a module rather than a line in each
 * screen because the API and the SPA have to reach the same answer — a
 * dashboard whose "trading week" label disagrees with the figures under it is
 * worse than no dashboard.
 *
 * Melbourne is UTC+10, or +11 through daylight saving. So `toISOString()` is
 * ten or eleven hours behind the venue, and a Sunday night's last entry —
 * logged at 11 pm, which is 13:00Z — reads as *Monday* to anything comparing
 * UTC. That is one closing night's wastage landing in next week's total, every
 * week, and a month that turns over at 10 or 11 in the morning. Hence this.
 *
 * Pure and dependency-free: `Intl.DateTimeFormat` with a `timeZone` is the
 * only zone database anywhere in the stack, and the runtime already ships it.
 */
/**
 * The zone a venue runs in unless its META says otherwise.
 *
 * One venue, one city, today. `timezoneOf` is what reads the override, so the
 * day a second city opens nothing has to change but the stored attribute.
 */
export const DEFAULT_TIMEZONE = "Australia/Melbourne";
/**
 * Hours after local midnight that still count as the previous day's service,
 * unless the venue sets its own (`rolloverHour` on its META).
 *
 * Three, by Rob's call (2026-10-04): clear of a 01:30 close, and a day's
 * figures that run 3 am to 3 am. Was four.
 */
export const SERVICE_ROLLOVER_HOUR = 3;
/**
 * Monday, unless the venue sets its own (`weekStartsOn`). Trading weeks run
 * Monday to Sunday, so a Friday night sits mid-week.
 */
export const WEEK_STARTS_ON = 1;
/** The cut-offs a venue may choose, midnight to six in the morning. */
export const ROLLOVER_HOURS = [0, 1, 2, 3, 4, 5, 6];
export const DEFAULT_CLOCK = {
    timeZone: DEFAULT_TIMEZONE,
    rolloverHour: SERVICE_ROLLOVER_HOUR,
    weekStartsOn: WEEK_STARTS_ON,
};
/** The zone name of a zone or a clock. */
export function zoneName(zone) {
    return typeof zone === "string" ? zone : zone.timeZone;
}
function rolloverOf(zone, explicit) {
    return explicit ?? (typeof zone === "string" ? SERVICE_ROLLOVER_HOUR : zone.rolloverHour);
}
function weekStartOf(zone, explicit) {
    return explicit ?? (typeof zone === "string" ? WEEK_STARTS_ON : zone.weekStartsOn);
}
export function isRolloverHour(value) {
    return typeof value === "number" && ROLLOVER_HOURS.includes(value);
}
export function isWeekday(value) {
    return typeof value === "number" && Number.isInteger(value) && value >= 0 && value <= 6;
}
/**
 * A venue's clock, off whatever carries its settings — the raw META in the
 * API, `venueState.venue` in the SPA — with the defaults for anything unset
 * or unreadable, as `timezoneOf` does for the zone.
 */
export function clockOf(source) {
    const s = (source ?? {});
    return {
        timeZone: timezoneOf(source),
        rolloverHour: isRolloverHour(s.rolloverHour) ? s.rolloverHour : SERVICE_ROLLOVER_HOUR,
        weekStartsOn: isWeekday(s.weekStartsOn) ? s.weekStartsOn : WEEK_STARTS_ON,
    };
}
// ------------------------------------------------------------ the zone itself
const FORMATTERS = new Map();
/**
 * A cached formatter for a zone.
 *
 * `en-GB` with `hourCycle: "h23"` so midnight is hour `00`: `en-CA` reads as
 * ISO already but still hands back `24` for midnight under `h24`, and an hour
 * of 24 would push every midnight into the day before.
 *
 * Constructing one of these is not cheap and the dashboard asks for the same
 * zone on every tick, so they are kept.
 */
function formatter(timeZone) {
    let found = FORMATTERS.get(timeZone);
    if (!found) {
        found = new Intl.DateTimeFormat("en-GB", {
            timeZone,
            hourCycle: "h23",
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
        });
        FORMATTERS.set(timeZone, found);
    }
    return found;
}
const KNOWN_ZONES = new Map();
/** Does the runtime's zone database know this name? */
export function isTimezone(value) {
    if (typeof value !== "string" || value === "")
        return false;
    const cached = KNOWN_ZONES.get(value);
    if (cached !== undefined)
        return cached;
    let ok = true;
    try {
        new Intl.DateTimeFormat("en-GB", { timeZone: value });
    }
    catch {
        ok = false;
    }
    KNOWN_ZONES.set(value, ok);
    return ok;
}
/**
 * The zone a venue trades in, off whatever carries it.
 *
 * Read structurally rather than off a type, because the shape differs by
 * caller: the API hands in the raw `META` item, the SPA hands in
 * `venueState.venue`, and neither is worth a shared interface for one
 * optional string. An unknown zone name falls back rather than throwing — a
 * typo in one venue's settings must not take its dashboard down.
 */
export function timezoneOf(source, fallback = DEFAULT_TIMEZONE) {
    const zone = source?.timezone;
    return isTimezone(zone) ? zone : fallback;
}
const pad = (n, width = 2) => String(n).padStart(width, "0");
/** Milliseconds for a civil date read as if it were UTC. Years below 100 included. */
function asUtc(y, m, d, hour = 0, minute = 0, second = 0) {
    const at = new Date(0);
    at.setUTCFullYear(y, m - 1, d);
    at.setUTCHours(hour, minute, second, 0);
    return at.getTime();
}
/** What the calendar and the clock in `tz` show at an instant. */
export function civilDate(at, tz = DEFAULT_TIMEZONE) {
    const found = {};
    for (const part of formatter(zoneName(tz)).formatToParts(at)) {
        if (part.type !== "literal")
            found[part.type] = Number(part.value);
    }
    const y = found["year"] ?? 0;
    const m = found["month"] ?? 1;
    const d = found["day"] ?? 1;
    return {
        y,
        m,
        d,
        hour: found["hour"] ?? 0,
        minute: found["minute"] ?? 0,
        second: found["second"] ?? 0,
        // From the civil date rather than the instant: the weekday a venue means
        // is the one on its own wall, which is a day ahead of UTC every evening.
        weekday: new Date(asUtc(y, m, d)).getUTCDay(),
    };
}
/** `YYYY-MM-DD` for a civil date. */
export function dateString(civil) {
    return `${pad(civil.y, 4)}-${pad(civil.m)}-${pad(civil.d)}`;
}
/** `YYYY-MM-DD` as the calendar in `tz` reads an instant. */
export function localDateIn(at = new Date(), tz = DEFAULT_TIMEZONE) {
    return dateString(civilDate(at, tz));
}
/** `YYYY-MM-DD` looks like a date. Does not check that the day exists. */
export function isDateString(value) {
    return typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);
}
/** `YYYY-MM-DD` shifted by whole days. Calendar arithmetic, no zone in it. */
export function addDays(date, days) {
    const at = new Date(`${date}T00:00:00Z`);
    if (Number.isNaN(at.getTime()))
        throw new RangeError(`Not a date: ${date}`);
    at.setUTCDate(at.getUTCDate() + days);
    return at.toISOString().slice(0, 10);
}
/** Day of the week for a `YYYY-MM-DD`, 0 = Sunday. */
export function weekdayOf(date) {
    const at = new Date(`${date}T00:00:00Z`);
    if (Number.isNaN(at.getTime()))
        throw new RangeError(`Not a date: ${date}`);
    return at.getUTCDay();
}
// --------------------------------------------------------------- the service
/**
 * The service date an instant belongs to, in the venue's zone: the local day,
 * with anything before `rolloverHour` counted back to the night before.
 *
 * `serviceDateIn(2026-09-19T15:30Z, "Australia/Melbourne")` — 01:30 on
 * Saturday morning there — is `2026-09-19`, Friday, because that is whose
 * shift is still running. It is the rule prep has always applied; it lives
 * here so the week and the month can apply it too.
 */
export function serviceDateIn(at = new Date(), tz = DEFAULT_TIMEZONE, rolloverHour) {
    const civil = civilDate(at, tz);
    const date = dateString(civil);
    return civil.hour >= rolloverOf(tz, rolloverHour) ? date : addDays(date, -1);
}
/** `YYYY-MM` for the calendar month an instant falls in, in `tz`. */
export function monthOf(at = new Date(), tz = DEFAULT_TIMEZONE) {
    // The calendar month, not the service month: a BAS period is calendar
    // months, and an invoice keyed in at 2 am on the first is that month's.
    const civil = civilDate(at, tz);
    return `${pad(civil.y, 4)}-${pad(civil.m)}`;
}
/** The trading week a service date sits in, on a venue's clock. */
export function weekOf(date, zone) {
    return weekBoundsOf(date, weekStartOf(zone, undefined));
}
/**
 * The trading week a service date sits in.
 *
 * `weekBoundsOf("2026-09-19")` — a Friday — is Monday the 14th through Sunday
 * the 20th. Takes a service date, not an instant, so the caller has already
 * decided which night a 01:30 tick belongs to.
 */
export function weekBoundsOf(date, startsOn = WEEK_STARTS_ON) {
    const back = (weekdayOf(date) - startsOn + 7) % 7;
    const start = addDays(date, -back);
    return { start, end: addDays(start, 6) };
}
/**
 * The calendar month a service date sits in.
 *
 * Calendar, not trading: a month that began mid-week is nobody's idea of a
 * month, and the figures people compare a month against — the one before,
 * the same one last year — are calendar months too.
 */
export function monthBoundsOf(date) {
    const start = `${date.slice(0, 7)}-01`;
    // The last day of the month is the day before the first of the next.
    const y = Number(date.slice(0, 4));
    const m = Number(date.slice(5, 7));
    const nextY = m === 12 ? y + 1 : y;
    const nextM = m === 12 ? 1 : m + 1;
    return { start, end: addDays(`${pad(nextY, 4)}-${pad(nextM)}-01`, -1) };
}
/**
 * The same month, a number of months earlier. `-12` is the year before,
 * which is what a month's figures are read against.
 */
export function monthShift(date, months) {
    const y = Number(date.slice(0, 4));
    const m = Number(date.slice(5, 7));
    const total = y * 12 + (m - 1) + months;
    return monthBoundsOf(`${pad(Math.floor(total / 12), 4)}-${pad((total % 12) + 1)}-01`);
}
/**
 * The trading week containing an instant, decided in the venue's zone.
 *
 * This is the fix for the whole issue in one function: 11 pm on Sunday in
 * Melbourne is 13:00Z on Sunday, and any comparison done in UTC puts it in
 * *next* week's figures under *this* week's label.
 */
export function weekBoundsIn(at = new Date(), tz = DEFAULT_TIMEZONE, options = {}) {
    return weekBoundsOf(serviceDateIn(at, tz, options.rolloverHour), weekStartOf(tz, options.startsOn));
}
// ------------------------------------------------------------ back to instants
/**
 * The instant at which a wall-clock time falls in a zone.
 *
 * There is no standard API for this, so it is solved by iteration: guess that
 * the wanted wall time is UTC, measure how far that guess lands from the
 * wanted wall time *in the zone*, and correct by the difference. Two passes
 * settle it even across a daylight-saving change, where the first correction
 * can carry the guess to the other side of the boundary and overshoot by an
 * hour.
 *
 * A local time that daylight saving skipped (2:30 am on the first Sunday in
 * October, in Melbourne) has no instant; the iteration settles on the instant
 * an hour later, which is the same reading `Temporal`'s `compatible` gives.
 */
export function civilInstant(date, hour, minute, tz = DEFAULT_TIMEZONE) {
    if (!isDateString(date))
        throw new RangeError(`Not a date: ${date}`);
    const wanted = Date.parse(`${date}T00:00:00Z`);
    if (Number.isNaN(wanted))
        throw new RangeError(`Not a date: ${date}`);
    const target = wanted + (hour * 60 + minute) * 60_000;
    let guess = target;
    for (let pass = 0; pass < 2; pass += 1) {
        const civil = civilDate(new Date(guess), tz);
        const shown = asUtc(civil.y, civil.m, civil.d, civil.hour, civil.minute, civil.second);
        guess = target - (shown - guess);
    }
    return new Date(guess);
}
/**
 * The instant a local civil date begins in `tz` — local midnight.
 *
 * What turns a range of dates into the ISO timestamps a `begins_with` or a
 * `between` on `EXPENSE#<ts>` / `WASTE#<ts>` / `COUNT#<ts>` needs.
 */
export function startOfDayInstant(date, tz = DEFAULT_TIMEZONE) {
    return civilInstant(date, 0, 0, tz);
}
/** The instant a service date's night opens: `rolloverHour` local, not midnight. */
export function startOfServiceInstant(date, tz = DEFAULT_TIMEZONE, rolloverHour) {
    return civilInstant(date, rolloverOf(tz, rolloverHour), 0, tz);
}
/**
 * A `from`/`to` pair as instants, for filtering stored timestamps.
 *
 * Both ends are optional and both are inclusive of the *service* they name,
 * because "to=2026-09-20" plainly means "including Sunday night" — and
 * Sunday night, in a bar, runs to the cut-off (three in the morning). A value that already
 * carries a time is passed through untouched: a caller who sent an instant
 * meant an instant.
 *
 * The end is the last millisecond before the next service opens, so the
 * routes keep comparing strings the way they always have.
 */
export function periodBounds(from, to, tz = DEFAULT_TIMEZONE, rolloverHour) {
    const start = isDateString(from)
        ? startOfServiceInstant(from, tz, rolloverHour).toISOString()
        : (from ?? null);
    const end = isDateString(to)
        ? new Date(startOfServiceInstant(addDays(to, 1), tz, rolloverHour).getTime() - 1).toISOString()
        : (to ?? null);
    return { from: start === "" ? null : start, to: end === "" ? null : end };
}
//# sourceMappingURL=time.js.map